import React, { type FC, memo, useCallback, useState } from "react";
import clsx from "clsx";
import Text from "../../../../../../shared-components/css-modules/toyota/atoms/Text";
import Button from "../../../../../../shared-components/css-modules/toyota/atoms/Button";
import Icon from "../../../../../../shared-components/css-modules/toyota/atoms/Icon";
import EcoLabel from "../../../../../../shared-components/css-modules/toyota/molecules/EcoLabel";
import cardStyles from "../../../../../../shared-components/css-modules/toyota/molecules/Card.module.scss";
import styles from "./Engine.module.scss";
import {
    getEcoBadgeType,
    getWLTPLabel,
    hasAlternativeEcoSpecFlow,
} from "../../../../../../../../common-deprecated/settings/utils/commonSettingUtils";
import { selectCarMotorization, setMotorization } from "../../../../../redux/slices/CarConfigCarSlice";
import Popover from "../../../../../../shared-components/css-modules/toyota/vendor/popover/Popover";
import SidePanel from "../../../../../../shared-components/css-modules/toyota/organisms/SidePanel";
import HybridLabel from "../../../../../../shared-components/css-modules/toyota/molecules/HybridLabel";
import EngineDetails from "./EngineDetails";
import { useCarConfigDispatch, useCarConfigSelector } from "../../../../../redux/store";
import { changeCarId } from "../../../../../utils/navigationUtils";
import {
    trackEngineMoreInfoClose,
    trackEngineMoreInfoOpen,
    trackEngineSelect,
    trackEngineTooltip,
} from "../../../../../utils/tracking";
import a11y from "../../../../../../shared-components/css-modules/toyota/utils/Accessibility.module.scss";
import EfficiencyClass from "../../../../../../shared-components/eco/EfficiencyClass";
import { getEcoTagType } from "../../../../../../../shared-logic/utils/typedUtils";
import { getEcoBadgeUrl } from "../../../../../../../../common-deprecated/utils";
import { CarConfigStep } from "../../../../../../../../common-deprecated/features/apheleia/utils/carConfigNavigationUtils";
import Price from "../../../common/price/Price";
import { MODAL_OTHER_GRADE } from "../../../../../utils/modalConstants";
import useAphModal from "../../../../../hooks/useAphModal";
import { AvailableInOtherGradeModalType } from "../../../../../utils/types/SectionTypes";
import { filterEcoSpecs } from "../../../../../../../../common-deprecated/utils/specs";
import { getMotorizationLabelConfig } from "../../../../../utils/motorizationUtils";
import type { MotorizationSpecType, MotorizationType } from "../../../../../../../shared-logic/types/Motorization";
import { ecoTagToLabel } from "../../../../../../../../common-deprecated/utils/ecoTagUtils";
import { CarConfigLabel, getCarConfigLabel } from "../../../../../utils/labelUtils";
import { setNavigationIds } from "../../../../../redux/slices/CarConfigNavigationSlice";
import { addNotification } from "../../../../../../../../common-deprecated/redux/actions/NotificationActions";
import SanitizedRenderer from "../../../../../../../../common-deprecated/components/SanitizedRenderer";

const EngineItem: FC<{ motorization: MotorizationType; engineOtherGrade?: boolean }> = ({
    motorization,
    engineOtherGrade,
}) => {
    const dispatch = useCarConfigDispatch();
    const [showDetails, setShowDetails] = useState<boolean>(false);

    const commonSettings = useCarConfigSelector((state) => state.commonSettings);
    const carConfigSettings = useCarConfigSelector((state) => state.carConfigSettings);
    const currentMotorization = useCarConfigSelector(selectCarMotorization);
    const car = useCarConfigSelector((state) => state.car);

    const alternativeEcoSpecFlow = hasAlternativeEcoSpecFlow(commonSettings);
    const wltpLabel = getWLTPLabel(
        commonSettings,
        motorization.engine.certification,
        motorization.engine.type.electric,
    );
    const efficiencyLabelConfig = getMotorizationLabelConfig(motorization, alternativeEcoSpecFlow);

    const isActive =
        currentMotorization.engine.id === motorization.engine.id &&
        currentMotorization.wheeldrive.id === motorization.wheeldrive.id &&
        currentMotorization.transmission.id === motorization.transmission.id;

    const { showModalHelper } = useAphModal();
    const onClickHandler = useCallback(() => {
        if (engineOtherGrade) {
            showModalHelper(MODAL_OTHER_GRADE, {
                availableInOtherGradesProps: {
                    section: AvailableInOtherGradeModalType.Engine,
                    motorization,
                },
            });
        } else {
            trackEngineSelect();
            // Update the URL with the new CarID due to the selected motorization
            // Also update this in redux as this does not happen automatically
            dispatch(setNavigationIds({ modelId: car.model.id, carId: motorization.carId }));
            changeCarId(CarConfigStep.Configure, car.model.id, motorization.carId);

            dispatch(
                setMotorization({
                    carShortId: motorization.carShortId,
                    engine: {
                        id: motorization.engine.id,
                        name: motorization.engine.name,
                        hybrid: motorization.engine.type.hybrid,
                        electric: motorization.engine.type.electric,
                        fuelCell: motorization.engine.type.fuelCell,
                        plugin: motorization.engine.type.plugin,
                        internalCode: motorization.engine.internalCode,
                        type: motorization.engine.type.name,
                        typeId: motorization.engine.type.id,
                        typeCode: motorization.engine.type.internalCode,
                    },
                    transmission: {
                        id: motorization.transmission.id,
                        internalCode: motorization.transmission.internalCode,
                        name: motorization.transmission.name,
                    },
                    wheeldrive: {
                        id: motorization.wheeldrive.id,
                        internalCode: motorization.wheeldrive.internalCode,
                        name: motorization.wheeldrive.name,
                        description: motorization.wheeldrive.description,
                    },
                }),
            );

            dispatch(
                addNotification([
                    {
                        title: getCarConfigLabel({ commonSettings, carConfigSettings }, "carConfigA11yEngineUpdated"),
                        description: getCarConfigLabel(
                            { commonSettings, carConfigSettings },
                            "carConfigA11yEngineUpdatedDescription",
                            {
                                engine: `${motorization.engine.name} ${motorization.transmission.name} ${motorization.wheeldrive.name}`,
                            },
                        ),
                        srOnly: true,
                    },
                ]),
            );
        }
    }, [motorization, dispatch]);

    const onWltpClick = useCallback((isOpen: boolean) => {
        if (isOpen) {
            trackEngineTooltip("wltp");
        }
    }, []);

    const ecoTag = getEcoTagType({
        id: motorization.engine.id,
        name: motorization.engine.name,
        hybrid: motorization.engine.type.hybrid,
        electric: motorization.engine.type.electric,
        fuelCell: motorization.engine.type.fuelCell,
        plugin: motorization.engine.type.plugin,
        internalCode: motorization.engine.internalCode,
        type: motorization.engine.type.name,
        typeId: motorization.engine.type.id,
        typeCode: motorization.engine.type.internalCode,
    });
    const ecoBadge = getEcoBadgeType(commonSettings, ecoTag, car.model.id);
    const ecoBadgeUrl = getEcoBadgeUrl(ecoBadge, commonSettings);
    const ecoTagLabel = ecoTagToLabel(ecoTag);

    const specs = alternativeEcoSpecFlow
        ? motorization.specs
        : filterEcoSpecs<MotorizationSpecType>(motorization.specs);

    return (
        <>
            <article
                className={clsx(cardStyles.card, cardStyles.clickable, styles.engine, isActive && cardStyles.active)}
                onClick={onClickHandler}
            >
                <div className={styles["engine-header"]}>
                    <div>
                        <div className={styles["engine-header-labels"]}>
                            {ecoBadgeUrl && <EcoLabel url={ecoBadgeUrl} />}
                            {efficiencyLabelConfig.show && <EfficiencyClass label={efficiencyLabelConfig.label} />}
                            {ecoTagLabel && (
                                <HybridLabel
                                    variant={ecoTag}
                                    label={getCarConfigLabel({ commonSettings, carConfigSettings }, ecoTagLabel)}
                                />
                            )}
                        </div>
                        <Text tag="h3" variant="header-body-regular" spacing="spacing-1">
                            {/*This is a button for a11y and should have an onclick to select this engine*/}
                            <Button variant="clean" aria-pressed={isActive} onClick={onClickHandler}>
                                {motorization.wheeldrive.name}
                            </Button>
                        </Text>
                    </div>
                </div>
                {motorization.engine.description && (
                    <Text variant="apheleia-body" spacing="spacing-2" tag="div">
                        <SanitizedRenderer html={motorization.engine.description} />
                    </Text>
                )}
                <Text variant="apheleia-body" spacing="spacing-0" className={styles.motorization}>
                    {motorization.engine.name}, {motorization.transmission.name}
                </Text>
                {specs.length !== 0 && (
                    <div className={styles.specs}>
                        {specs.map((spec) => (
                            <Text variant="apheleia-body" className={styles.spec} key={spec.id}>
                                {spec.name}
                                {(wltpLabel || spec.tooltip) && (
                                    <Popover placement="top" onOpenChangeListen={onWltpClick}>
                                        <Popover.Trigger>
                                            <Button
                                                variant="clean"
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                }}
                                            >
                                                <Icon variant="info" sizing="md" aria-hidden="true" />
                                                <span className={a11y["sr-only"]}>
                                                    <CarConfigLabel label="carConfigA11yButtonToggleFuelInfo" />
                                                </span>
                                            </Button>
                                        </Popover.Trigger>
                                        <Popover.Content>
                                            <SanitizedRenderer html={spec.tooltip || wltpLabel} as="div" />
                                        </Popover.Content>
                                    </Popover>
                                )}
                                <br />
                                <strong>
                                    {spec.value} {spec.unit}
                                </strong>
                            </Text>
                        ))}
                    </div>
                )}
                <Button
                    variant="apheleia-text"
                    onClick={(event) => {
                        event.stopPropagation();
                        trackEngineMoreInfoOpen();
                        setShowDetails(true);
                    }}
                >
                    <CarConfigLabel label="carConfigEngineLearnMore" />
                </Button>
                <div className={styles.footer}>
                    {!engineOtherGrade && (
                        <div className={styles.pricing}>
                            <Price
                                price={motorization.price}
                                customFormatting={carConfigSettings.customStartingPriceFormatting}
                            />
                        </div>
                    )}
                    <div className={styles.indicator}>
                        {isActive ? (
                            <Icon variant="check" sizing="xs" aria-hidden="true" />
                        ) : (
                            <Icon variant="plus" sizing="xs" aria-hidden="true" />
                        )}
                    </div>
                </div>
            </article>

            <SidePanel
                open={showDetails}
                onRequestClose={() => {
                    trackEngineMoreInfoClose();
                    setShowDetails(false);
                }}
            >
                <EngineDetails motorization={motorization} />
            </SidePanel>
        </>
    );
};

export default memo(EngineItem);
